import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Bus, 
  Save, 
  Loader2,
  Users,
  Clock,
  Info,
  Briefcase,
  Sun,
  Palmtree,
  MapPin,
  Globe,
  Calculator,
  Settings,
  Calendar,
  Plus,
  X,
  PartyPopper
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function SalarySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeRegion, setActiveRegion] = useState('domestic');
  const [activeTab, setActiveTab] = useState('rates');
  const [holidays, setHolidays] = useState([]);
  const [customHolidays, setCustomHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: '', name: '' });
  
  // Driver overrides
  const [driverOverrides, setDriverOverrides] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [driverRates, setDriverRates] = useState({
    small: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
    medium: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
    large: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } }
  });
  const [savingDriverRates, setSavingDriverRates] = useState(false);
  
  const [settings, setSettings] = useState({
    small: { 
      min_seats: 1, 
      max_seats: 23, 
      label: 'До 23 мест',
      domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 },
      international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }
    },
    medium: { 
      min_seats: 24, 
      max_seats: 34, 
      label: '24-34 мест',
      domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 },
      international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }
    },
    large: { 
      min_seats: 35, 
      max_seats: 60, 
      label: '35-60 мест',
      domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 },
      international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }
    }
  });
  
  const [formulaSettings, setFormulaSettings] = useState({
    base_hours: 10,
    overtime_bonus: 5,
    multiplier_threshold: 15,
    multiplier: 1.5
  });

  useEffect(() => {
    fetchSettings();
    fetchHolidays();
    fetchDriverOverrides();
  }, []);

  const fetchDriverOverrides = async () => {
    try {
      const response = await axios.get(`${API}/api/salary-settings/driver-overrides`, getAuthHeaders());
      setDriverOverrides(response.data.overrides || []);
      setAvailableDrivers(response.data.drivers || []);
    } catch (error) {
      console.error('Error fetching driver overrides:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const year = new Date().getFullYear();
      const response = await axios.get(`${API}/api/holidays/${year}`, getAuthHeaders());
      setHolidays(response.data.holidays || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/salary-settings`, getAuthHeaders());
      if (response.data.hourly_rates) {
        // Handle both old and new format
        const rates = response.data.hourly_rates;
        const hasNewFormat = rates.small?.domestic !== undefined;
        
        if (hasNewFormat) {
          setSettings(rates);
        } else {
          // Convert old format to new
          const converted = {};
          Object.keys(rates).forEach(key => {
            converted[key] = {
              ...rates[key],
              domestic: {
                weekday_rate: rates[key].weekday_rate || 0,
                weekend_rate: rates[key].weekend_rate || 0,
                weekend_season_rate: rates[key].weekend_season_rate || 0
              },
              international: {
                weekday_rate: 0,
                weekend_rate: 0,
                weekend_season_rate: 0
              }
            };
          });
          setSettings(converted);
        }
      }
      if (response.data.formula_settings) {
        setFormulaSettings(response.data.formula_settings);
      }
      if (response.data.custom_holidays) {
        setCustomHolidays(response.data.custom_holidays);
      }
    } catch (error) {
      console.error('Error fetching salary settings:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (category, region, rateType, value) => {
    const numValue = parseFloat(value) || 0;
    setSettings(prev => ({
      ...prev,
      [category]: { 
        ...prev[category], 
        [region]: {
          ...prev[category][region],
          [rateType]: numValue
        }
      }
    }));
  };

  const handleFormulaChange = (field, value) => {
    const numValue = parseFloat(value) || 0;
    setFormulaSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/salary-settings`, {
        hourly_rates: settings,
        formula_settings: formulaSettings,
        season_months: [5, 6, 7, 8, 9],
        custom_holidays: customHolidays
      }, getAuthHeaders());
      toast.success('Настройки сохранены');
      fetchHolidays(); // Refresh holidays list
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(error.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  const addCustomHoliday = () => {
    if (!newHoliday.date || !newHoliday.name) {
      toast.error('Укажите дату и название праздника');
      return;
    }
    setCustomHolidays(prev => [...prev, { ...newHoliday }]);
    setNewHoliday({ date: '', name: '' });
  };

  const removeCustomHoliday = (index) => {
    setCustomHolidays(prev => prev.filter((_, i) => i !== index));
  };

  // Save individual driver rates
  const saveDriverRates = async () => {
    if (!selectedDriverId) {
      toast.error('Выберите водителя');
      return;
    }
    setSavingDriverRates(true);
    try {
      await axios.post(
        `${API}/api/salary-settings/driver-overrides`,
        { driver_id: selectedDriverId, hourly_rates: driverRates },
        getAuthHeaders()
      );
      toast.success('Индивидуальные ставки сохранены');
      fetchDriverOverrides();
      setSelectedDriverId('');
    } catch (error) {
      toast.error('Ошибка сохранения');
    } finally {
      setSavingDriverRates(false);
    }
  };

  // Delete driver override
  const deleteDriverOverride = async (driverId) => {
    if (!confirm('Удалить индивидуальные ставки? Водитель будет использовать общие ставки.')) return;
    try {
      await axios.delete(`${API}/api/salary-settings/driver-overrides/${driverId}`, getAuthHeaders());
      toast.success('Индивидуальные ставки удалены');
      fetchDriverOverrides();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  // Load driver rates for editing
  const editDriverOverride = (override) => {
    setSelectedDriverId(override.driver_id);
    setDriverRates(override.hourly_rates || {
      small: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
      medium: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
      large: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } }
    });
  };

  // Update driver rate value
  const updateDriverRate = (category, region, rateType, value) => {
    setDriverRates(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [region]: {
          ...prev[category]?.[region],
          [rateType]: parseFloat(value) || 0
        }
      }
    }));
  };

  // Calculate salary based on formula
  const calculateSalary = (hours, rate) => {
    const { base_hours, overtime_bonus, multiplier_threshold, multiplier } = formulaSettings;
    
    if (hours <= base_hours) {
      return hours * rate;
    }
    
    const overtimeHours = hours - base_hours;
    const basePay = base_hours * rate;
    const overtimePay = overtimeHours * (rate + overtime_bonus);
    const total = basePay + overtimePay;
    
    if (hours >= multiplier_threshold) {
      return total * multiplier;
    }
    
    return total;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const categories = [
    { key: 'small', icon: '🚐', color: 'border-green-300', headerBg: 'bg-green-100' },
    { key: 'medium', icon: '🚌', color: 'border-blue-300', headerBg: 'bg-blue-100' },
    { key: 'large', icon: '🚎', color: 'border-purple-300', headerBg: 'bg-purple-100' }
  ];

  const rateTypes = [
    { key: 'weekday_rate', label: 'Пн-Пт', icon: Briefcase, color: 'text-gray-700', bg: 'bg-gray-50' },
    { key: 'weekend_rate', label: 'Сб-Вс', icon: Sun, color: 'text-orange-600', bg: 'bg-orange-50' },
    { key: 'weekend_season_rate', label: 'Сезон', icon: Palmtree, color: 'text-green-600', bg: 'bg-green-50' }
  ];

  const regions = [
    { key: 'domestic', label: 'Литва', icon: MapPin, color: 'bg-blue-600' },
    { key: 'international', label: 'Заграница', icon: Globe, color: 'bg-purple-600' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600" />
            Почасовые ставки
          </h2>
          <p className="text-gray-500 mt-1">
            Настройка стоимости часа работы водителя
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-green-600 hover:bg-green-700"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Сохранить
        </Button>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-4">
          <TabsTrigger value="rates" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Ставки
          </TabsTrigger>
          <TabsTrigger value="drivers" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Инд. ставки
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <PartyPopper className="w-4 h-4" />
            Праздники
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rates" className="space-y-6">
          {/* Formula Settings Card */}
          <Card className="border-2 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-5 h-5 text-indigo-600" />
            Формула расчёта
          </CardTitle>
          <CardDescription>
            Базовые часы → Переработка → Множитель
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1">
                <Clock className="w-4 h-4 text-indigo-500" />
                Базовые часы
              </Label>
              <Input
                type="number"
                min="1"
                value={formulaSettings.base_hours}
                onChange={(e) => handleFormulaChange('base_hours', e.target.value)}
                className="text-center font-semibold"
              />
              <p className="text-xs text-gray-500">Без доплаты</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-green-500" />
                Доплата/час
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={formulaSettings.overtime_bonus}
                  onChange={(e) => handleFormulaChange('overtime_bonus', e.target.value)}
                  className="text-center font-semibold pr-7"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              </div>
              <p className="text-xs text-gray-500">Сверх базовых</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1">
                <Clock className="w-4 h-4 text-orange-500" />
                Порог множителя
              </Label>
              <Input
                type="number"
                min="1"
                value={formulaSettings.multiplier_threshold}
                onChange={(e) => handleFormulaChange('multiplier_threshold', e.target.value)}
                className="text-center font-semibold"
              />
              <p className="text-xs text-gray-500">Часов для ×</p>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-1">
                <span className="text-red-500 font-bold">×</span>
                Множитель
              </Label>
              <Input
                type="number"
                min="1"
                step="0.1"
                value={formulaSettings.multiplier}
                onChange={(e) => handleFormulaChange('multiplier', e.target.value)}
                className="text-center font-semibold"
              />
              <p className="text-xs text-gray-500">При превышении</p>
            </div>
          </div>
          
          {/* Formula explanation */}
          <div className="mt-4 p-3 bg-white/70 rounded-lg text-sm">
            <p className="font-medium text-gray-700 mb-2">Как работает:</p>
            <ul className="space-y-1 text-gray-600">
              <li>• До <strong>{formulaSettings.base_hours}ч</strong>: часы × ставка</li>
              <li>• {formulaSettings.base_hours}-{formulaSettings.multiplier_threshold}ч: ({formulaSettings.base_hours} × ставка) + (переработка × (ставка + {formulaSettings.overtime_bonus}€))</li>
              <li>• {formulaSettings.multiplier_threshold}+ч: всё × <strong>{formulaSettings.multiplier}</strong></li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Region Tabs */}
      <Tabs value={activeRegion} onValueChange={setActiveRegion} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          {regions.map(r => (
            <TabsTrigger 
              key={r.key} 
              value={r.key}
              className={`flex items-center gap-2 data-[state=active]:${r.color} data-[state=active]:text-white`}
            >
              <r.icon className="w-4 h-4" />
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {regions.map(region => (
          <TabsContent key={region.key} value={region.key}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <region.icon className="w-5 h-5" />
                  Ставки: {region.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-4 font-semibold text-gray-700 w-48">
                          <div className="flex items-center gap-2">
                            <Bus className="w-4 h-4" />
                            Категория
                          </div>
                        </th>
                        {rateTypes.map(rt => (
                          <th key={rt.key} className="text-center p-4 font-semibold text-gray-700">
                            <div className={`flex flex-col items-center gap-1 ${rt.color}`}>
                              <rt.icon className="w-5 h-5" />
                              <span className="text-xs">{rt.label}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {categories.map(({ key, icon, color, headerBg }) => {
                        const cat = settings[key];
                        const regionRates = cat[region.key] || { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 };
                        return (
                          <tr key={key} className={`border-b ${color} border-l-4`}>
                            <td className={`p-4 ${headerBg}`}>
                              <div className="flex items-center gap-2">
                                <span className="text-2xl">{icon}</span>
                                <div>
                                  <div className="font-semibold text-gray-800">{cat.label}</div>
                                  <div className="text-xs text-gray-500 flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {cat.min_seats === 1 ? 'До' : `${cat.min_seats}-`}{cat.max_seats} мест
                                  </div>
                                </div>
                              </div>
                            </td>
                            {rateTypes.map(rt => (
                              <td key={rt.key} className={`p-3 ${rt.bg}`}>
                                <div className="relative max-w-[120px] mx-auto">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.5"
                                    value={regionRates[rt.key] || ''}
                                    onChange={(e) => handleRateChange(key, region.key, rt.key, e.target.value)}
                                    placeholder="0.00"
                                    className="pr-7 text-center font-semibold"
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                    €
                                  </span>
                                </div>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      {/* Example Calculations */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            Примеры расчёта (ставка 10€)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { hours: 8, label: `${8} часов` },
              { hours: 12, label: `${12} часов` },
              { hours: 16, label: `${16} часов` }
            ].map(({ hours, label }) => {
              const salary = calculateSalary(hours, 10);
              const isOvertime = hours > formulaSettings.base_hours;
              const isMultiplied = hours >= formulaSettings.multiplier_threshold;
              
              return (
                <div key={hours} className={`p-4 rounded-xl ${
                  isMultiplied ? 'bg-red-50 border-2 border-red-200' : 
                  isOvertime ? 'bg-orange-50 border-2 border-orange-200' : 
                  'bg-gray-50 border-2 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-700">{label}</span>
                    {isMultiplied && <span className="text-xs bg-red-200 text-red-700 px-2 py-0.5 rounded-full">×{formulaSettings.multiplier}</span>}
                    {isOvertime && !isMultiplied && <span className="text-xs bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full">+{formulaSettings.overtime_bonus}€/ч</span>}
                  </div>
                  <div className="text-2xl font-bold text-gray-800">
                    {salary.toFixed(2)}€
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {hours <= formulaSettings.base_hours ? (
                      `${hours} × 10€`
                    ) : hours < formulaSettings.multiplier_threshold ? (
                      `(${formulaSettings.base_hours}×10) + (${hours - formulaSettings.base_hours}×${10 + formulaSettings.overtime_bonus})`
                    ) : (
                      `((${formulaSettings.base_hours}×10) + (${hours - formulaSettings.base_hours}×${10 + formulaSettings.overtime_bonus})) × ${formulaSettings.multiplier}`
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Типы дней:</p>
                <ul className="mt-1 space-y-1 text-blue-700">
                  <li><strong>Пн-Пт</strong> — рабочие дни</li>
                  <li><strong>Сб-Вс</strong> — выходные и праздники</li>
                  <li><strong>Сезон</strong> — выходные/праздники май-сентябрь</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Bus className="w-5 h-5 text-orange-600 mt-0.5" />
              <div className="text-sm text-orange-800">
                <p className="font-medium">Не забудьте:</p>
                <p className="text-orange-700">
                  Укажите количество мест для каждого автобуса в разделе Автобусы
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TabsContent>

    {/* Individual Driver Rates Tab */}
    <TabsContent value="drivers" className="space-y-6">
      <Card className="bg-purple-50 border-purple-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-purple-800">
              <p className="font-medium">Индивидуальные ставки</p>
              <p className="text-purple-700">
                Здесь можно задать особые ставки для отдельных водителей. 
                Если у водителя нет индивидуальных ставок, используются общие.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Overrides */}
      {driverOverrides.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Водители с особыми ставками</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {driverOverrides.map((override) => (
                <div key={override.driver_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{override.driver_name}</span>
                    <span className="text-gray-500 text-sm ml-2">
                      (обновлено: {new Date(override.updated_at).toLocaleDateString('ru-RU')})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => editDriverOverride(override)}>
                      Изменить
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteDriverOverride(override.driver_id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Driver Rates */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {selectedDriverId ? 'Редактировать ставки' : 'Добавить водителя'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Driver Select */}
          <div className="space-y-2">
            <Label>Водитель</Label>
            <select
              value={selectedDriverId}
              onChange={(e) => {
                setSelectedDriverId(e.target.value);
                // Reset rates when selecting new driver
                if (!driverOverrides.find(d => d.driver_id === e.target.value)) {
                  setDriverRates({
                    small: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
                    medium: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } },
                    large: { domestic: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 }, international: { weekday_rate: 0, weekend_rate: 0, weekend_season_rate: 0 } }
                  });
                }
              }}
              className="w-full p-2 border rounded-lg"
            >
              <option value="">Выберите водителя...</option>
              {availableDrivers.map((driver) => (
                <option key={driver.id} value={driver.id}>
                  {driver.name || driver.username}
                </option>
              ))}
            </select>
          </div>

          {selectedDriverId && (
            <>
              {/* Region Toggle */}
              <div className="flex rounded-lg bg-gray-100 p-1 w-fit">
                <button
                  onClick={() => setActiveRegion('domestic')}
                  className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                    activeRegion === 'domestic' ? 'bg-white shadow text-blue-600 font-medium' : 'text-gray-600'
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  Местные
                </button>
                <button
                  onClick={() => setActiveRegion('international')}
                  className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 ${
                    activeRegion === 'international' ? 'bg-white shadow text-purple-600 font-medium' : 'text-gray-600'
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  Международные
                </button>
              </div>

              {/* Rate Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { key: 'small', label: 'До 23 мест', icon: '🚐' },
                  { key: 'medium', label: '24-34 мест', icon: '🚌' },
                  { key: 'large', label: '35-60 мест', icon: '🚎' }
                ].map((cat) => (
                  <div key={cat.key} className="border rounded-lg p-3 space-y-3">
                    <div className="font-medium flex items-center gap-2">
                      <span>{cat.icon}</span>
                      {cat.label}
                    </div>
                    {[
                      { key: 'weekday_rate', label: 'Пн-Пт' },
                      { key: 'weekend_rate', label: 'Сб-Вс' },
                      { key: 'weekend_season_rate', label: 'Сезон' }
                    ].map((rate) => (
                      <div key={rate.key} className="flex items-center gap-2">
                        <Label className="w-16 text-sm">{rate.label}</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          value={driverRates[cat.key]?.[activeRegion]?.[rate.key] || 0}
                          onChange={(e) => updateDriverRate(cat.key, activeRegion, rate.key, e.target.value)}
                          className="w-24 text-center"
                        />
                        <span className="text-gray-500">€/ч</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Save Button */}
              <Button onClick={saveDriverRates} disabled={savingDriverRates} className="w-full">
                {savingDriverRates ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Сохранить ставки
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </TabsContent>

    {/* Holidays Tab */}
    <TabsContent value="holidays" className="space-y-6">
          {/* Info */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Праздничные дни = Выходные</p>
                  <p className="text-amber-700">
                    В праздничные дни применяется тариф выходного дня (Сб-Вс). 
                    Литовские праздники определяются автоматически, включая Пасху.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Auto Holidays */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Литовские праздники {new Date().getFullYear()}
              </CardTitle>
              <CardDescription>
                Определяются автоматически
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {holidays.filter(h => h.type === 'auto').map((h, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                    <span className="text-sm font-mono text-blue-600">{h.date.slice(5)}</span>
                    <span className="text-sm text-gray-700">{h.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Custom Holidays */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Дополнительные праздники
              </CardTitle>
              <CardDescription>
                Добавьте свои праздничные дни
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add new holiday form */}
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Дата (ГГГГ-ММ-ДД или ММ-ДД)</Label>
                  <Input
                    placeholder="2025-12-31 или 12-31"
                    value={newHoliday.date}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Название</Label>
                  <Input
                    placeholder="Название праздника"
                    value={newHoliday.name}
                    onChange={(e) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <Button onClick={addCustomHoliday} size="sm" className="h-9">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Формат ММ-ДД (например 12-31) — повторяется каждый год. 
                Формат ГГГГ-ММ-ДД — конкретная дата.
              </p>

              {/* Custom holidays list */}
              {customHolidays.length > 0 && (
                <div className="space-y-2 pt-2 border-t">
                  {customHolidays.map((h, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono text-green-600">{h.date}</span>
                        <span className="text-sm text-gray-700">{h.name}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => removeCustomHoliday(idx)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
